import React, { useState, useEffect, useContext, useMemo } from "react";
import { GlobalContext } from "./GlobalContext";
import { AddEditCommitted } from "./lazyForms";
import CommonDataView from "./CommonDataView";
import useTabularDataManager from "./useTabularDataManager";
import useFieldOrder from "./useFieldOrder";
import MessageSelected from "./MessageSelected";
import { Button } from "@radix-ui/themes";
import FloatingDownloadButton from "./FloatingDownloadButton";
import CollectionTargetBar from "./CollectionTargetBar";
import { formatUGX } from "./UtilityFunctions";

function Credit({ cars, setCars, initialSelectedRow = "" , isLoading = false }){
    const ownSheetName = "Credit";

    const {
        modalVisible, setModalVisibility,
        messagesModalVisible, setMessagesModalVisible,
        sortOrder, sortData,
        filterObject, filterData,
        filteredDataArray
        } = useTabularDataManager(cars, setCars);    

    return (
        <div className="flex flex-col h-full sheet-viewport" id="credit-content">
            <Content isLoading={isLoading}
                modalVisible={modalVisible}
                setModalVisibility={setModalVisibility}
                filteredDataArray={filteredDataArray} setCars={setCars} sortData={sortData}
                sortOrder={sortOrder} parentSheetName={ownSheetName}
                filterData={filterData} filterObject={filterObject}
                ownSheetName={ownSheetName}
                initialSelectedRow={initialSelectedRow}
            />
            <Summary 
              setModalVisibility={setModalVisibility} 
              filteredDataArray={filteredDataArray} 
              setMessagesModalVisible={setMessagesModalVisible}/>
            {
              messagesModalVisible && 
                  <MessageSelected
                      onClose={()=>{setMessagesModalVisible(false)}}
                      recordsData={filteredDataArray}
                      sheetName={ownSheetName}
                  />
            }
        </div>
    )
}

function Content({ filteredDataArray, setCars, modalVisible, setModalVisibility,
   sortData, sortOrder, parentSheetName, filterData, filterObject, ownSheetName, initialSelectedRow = "", isLoading }){
    const [fieldOrder, setFieldOrder] = useFieldOrder(parentSheetName, filteredDataArray[0], ['Make', 'Model', 'ModelYear', 'CustomerName', 'TotalBalance', 'DueDate']);
    
    const [ currentVehicleIndex, setCurrentVehicle ] = useState();
    const openModalEdit = (vehicleId)=>{
        setCurrentVehicle(vehicleId);
        setModalVisibility({
            visible: true,
            mode: "edit"
        });
    }

    const [ selectedRow, setSelectedRow ] = useState(initialSelectedRow)

    useEffect(() => {
        if (initialSelectedRow) setSelectedRow(initialSelectedRow);
    }, [filteredDataArray, initialSelectedRow]);

    const selectRow = (rowIndex)=>{
        setSelectedRow(rowIndex)
    }

    // Closures reference no component state — stable for the component lifetime.
    const formattingRules = useMemo(() => ({
        Days: {
            formatByValue: (val)=>{
                if(val > 240 && val <= 270){
                    return {color: "orange", fontWeight: "bold"}
                }
                if(val > 270){
                    return {color: "red", fontWeight: "bold"}
                }
                return {color: "green", fontWeight: "bold"}
            }
        },
        DaysCommitted: {
            formatByValue: (val)=>{
                if(val > 30 && val <= 60){
                    return {color: "orange", fontWeight: "bold", textAlign: "center"}
                }
                if(val > 60){
                    return {color: "red", fontWeight: "bold", textAlign: "center"}
                }
                return {color: "green", fontWeight: "bold", textAlign: "center"}
            }
        }
    }), []);

    return (
        <div className="flex-grow overflow-auto min-h-0">
            <CommonDataView
                isLoading={isLoading}
                objectsArray={filteredDataArray}
                sortData={sortData}
                sortOrder={sortOrder}
                fieldOrder={fieldOrder}
                setFieldOrder={setFieldOrder}
                parentSheetName={parentSheetName}
                filterData={filterData}
                filterObject={filterObject}
                sheetName={ownSheetName}
                selectRow={selectRow}
                selectedRow={selectedRow}
                openModalEdit={openModalEdit}
                primaryKeyName={"VehicleID"}
                formattingRules={formattingRules}
                searchPlaceholder="Search credit sales…"
            />
            {
                modalVisible.visible &&  filteredDataArray[currentVehicleIndex] &&
                <AddEditCommitted 
                    formMode={modalVisible.mode} 
                    showModal={setModalVisibility} 
                    car={filteredDataArray[currentVehicleIndex]} 
                    parentSheetName={parentSheetName} 
                    //In add mode, read the origin that the user selected. In edit mode, read the origin from the record's origin field
                    origin={modalVisible.mode==="add" ? modalVisible.origin : filteredDataArray[currentVehicleIndex].Origin}
                />    
            }
            <FloatingDownloadButton 
                data={filteredDataArray}
                fieldOrder={fieldOrder}
                sheetName={ownSheetName}
                filterObject={filterObject}
            />     
        </div>
    )
}

function Summary({ filteredDataArray, setMessagesModalVisible }){
    const { can } = useContext(GlobalContext);
    return (
        <div className="h-14 md:h-[80px] flex-none bg-white border-t flex gap-4 items-center px-4" id="credit-summary">
            {can('messages.send') && <Button
                id="message-filtered-button"
                className="ml-2"
                variant="soft"
                size="2"
                radius="medium"
                //onClick={()=>{setMessagesModalVisible(true)}}
                onClick={()=>{setMessagesModalVisible(true)}}
                title="Send messages to all customers in the current filtered list"
            >Send Messages</Button>}
            {/* Unlike the stat cards beside it, this one is dealership-wide and
                ignores the sheet's filters — see the header of
                CollectionTargetBar.jsx for why it has to be. */}
            <CollectionTargetBar />
            <CreditSalesStatsBar
              className="border-none shadow-none ml-2"
              filteredDataArray={filteredDataArray} />
        </div>
    )
}

import { Card, Metric, Text, Flex, Grid } from "@tremor/react";

function CreditSalesStatsBar({ filteredDataArray }) {
  const [stats, setStats] = useState({
    totalVehicles: 0,
    totalValue: 0,
    totalPaid: 0,
    totalBalance: 0,
    overdueCount: 0,
    totalOverduePenalties: 0
  });

  useEffect(() => {
    if (!filteredDataArray || filteredDataArray.length === 0) return;
    
    const totalVehicles = filteredDataArray.length;
    const totalValue = filteredDataArray.reduce((sum, car) => sum + (Number(car.AgreedPrice) || 0), 0);
    const totalPaid = filteredDataArray.reduce((sum, car) => sum + (Number(car.TotalPaid) || 0), 0);
    const totalBalance = filteredDataArray.reduce((sum, car) => sum + (Number(car.TotalBalance) || 0), 0);
    
    // Count overdue vehicles (DaysOverdue > 0)
    const overdueVehicles = filteredDataArray.filter(car => (car.DaysOverdue || 0) > 0);
    const overdueCount = overdueVehicles.length;
    
    // Calculate total penalties from overdue payments
    const totalOverduePenalties = filteredDataArray.reduce((sum, car) => sum + (Number(car.TotalPenalty) || 0), 0);
    
    setStats({
      totalVehicles,
      totalValue,
      totalPaid,
      totalBalance,
      overdueCount,
      totalOverduePenalties
    });
  }, [filteredDataArray]);

  //Shared formatter — see formatUGX in UtilityFunctions.jsx.
  const formatCurrency = (value) => formatUGX(value);

  return (
    //Stats cards overflow their band on phones — desktop only for now
    <div className="bg-white rounded-md h-18 hidden md:block" id="credit-stats">
      <Grid numItemsSm={3} numItemsLg={6} className="gap-2 h-full">
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Total Number</Text>
          <Metric className="text-lg">{stats.totalVehicles}</Metric>
        </Card>
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Outstanding</Text>
          <Metric className="text-lg">{formatCurrency(stats.totalBalance)}</Metric>
        </Card>
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500 flex items-center">
            Overdue Customers
            {stats.overdueCount > 0 && (
              <span className="ml-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </Text>
          <Metric className={`text-lg ${stats.overdueCount > 0 ? 'text-red-500' : ''}`}>
            {stats.overdueCount}
          </Metric>
        </Card>
        <Card className="p-2 flex flex-col justify-center items-center h-full">
          <Text className="text-xs font-medium text-gray-500">Penalties</Text>
          <Metric className="text-lg">{formatCurrency(stats.totalOverduePenalties)}</Metric>
        </Card>
      </Grid>
    </div>
  );
}

export default Credit;